import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled, { keyframes } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import {
  COLOR_PRIMARY,
  COLOR_LIGHT_RED,
  COLOR_RED,
} from "../../constants/color";
import { SelectDropdown } from "./SelectDropdown";
import { ArrowIcon } from "./icons/ArrowIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";

export type SelectOptionType = {
  id: number | string;
  name: string;
  value: any;
};

type Props = {
  name: string;
  options: SelectOptionType[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  onChange: (name: string, value: any) => void;
  clearable?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  width?: string;
  hasSearchInput?: boolean;
};

export const Select: React.FC<Props> = ({
  name,
  label,
  placeholder = "--",
  required,
  defaultValue,
  options,
  disabled,
  onChange,
  clearable = true,
  hasError,
  errorMessage,
  width = "100%",
  hasSearchInput = true,
}) => {
  const uuid = useMemo(() => {
    return uuidv4();
  }, []);

  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [currentName, setCurrentName] = useState<string>(defaultValue ?? "");

  const formContext = useFormContext();

  const handleOpenSelection = useCallback(() => {
    if (disabled) return;

    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleChange = useCallback(
    (optionName: string, value: string) => {
      setIsOpen(false);

      if (clearable && currentName === optionName) {
        setCurrentName(placeholder ?? "");
        onChange("", "");
        try {
          if (name)
            formContext.setValue(name, undefined, { shouldValidate: true });
        } catch (err) { }
        return;
      }

      setCurrentName(optionName);
      onChange(optionName, value);
      try {
        if (name) formContext.setValue(name, value, { shouldValidate: true });
      } catch (err) { }
    },
    [currentName, formContext],
  );

  useEffect(() => {
    try {
      if (!formContext.getValues(name)) setCurrentName(placeholder ?? "");
    } catch (err) { }
  }, [options]);

  // Handle defaultValue changes and options loading
  useEffect(() => {
    if (defaultValue && options.length > 0) {
      // Try exact match first
      let selectedOption = options.find(option => option.value === defaultValue);

      // If no exact match, try with type coercion
      if (!selectedOption) {
        selectedOption = options.find(option => String(option.value) === String(defaultValue));
      }

      if (selectedOption) {
        setCurrentName(selectedOption.name);
      } else {
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('Select component - no match found:', {
            name,
            defaultValue,
            defaultValueType: typeof defaultValue,
            options: options.map(opt => ({ value: opt.value, valueType: typeof opt.value, name: opt.name }))
          });
        }
      }
    }
  }, [defaultValue, options, name]);

  useEffect(() => {
    const subscription = formContext?.watch((value, { name: fieldName }) => {
      // Handle reset methods
      if (!fieldName) {
        const selectedOption = options.find(
          (option) => option.value === value[name],
        );
        setCurrentName(
          selectedOption ? selectedOption.name : (placeholder ?? ""),
        );
      }
    });

    return () => subscription?.unsubscribe();
  }, [formContext]);

  const closeDropdown = useCallback((e: MouseEvent) => {
    // @ts-expect-error
    const id = e.target?.id ?? "";

    if (id.includes(uuid)) return;

    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) document.body.addEventListener("click", closeDropdown);
    else document.body.removeEventListener("click", closeDropdown);

    return () => {
      document.body.removeEventListener("click", closeDropdown);
    };
  }, [isOpen]);

  const fillColor = useMemo(() => {
    if (disabled) return "#CCCCCC";

    if (hasError) return COLOR_RED;

    return "#333333";
  }, [disabled, hasError]);

  return (
    <StyledContainer
      id={`container-${uuid}`}
      $disabled={disabled}
      $width={width}
    >
      <StyledHeader>
        {label && (
          <StyledLabel>
            {required ? (
              <>
                {label.replace(' *', '')} <StyledRequiredAsterisk>*</StyledRequiredAsterisk>
              </>
            ) : (
              label
            )}
          </StyledLabel>
        )}
      </StyledHeader>

      <StyledSelect
        id={`select-container-${uuid}`}
        onClick={handleOpenSelection}
        $disabled={disabled}
        $hasError={hasError}
      >
        <StyledPlaceholder
          id={`select-placeholder-${uuid}`}
          $isPlaceholder={currentName === "" || currentName === placeholder}
        >
          {currentName !== "" ? currentName : placeholder}
        </StyledPlaceholder>

        <StyledArrowIcon id={`select-dropdown-${uuid}`} $isOpen={isOpen}>
          <ArrowIcon uuid={uuid} fillColor={fillColor} />
        </StyledArrowIcon>
      </StyledSelect>

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      <SelectDropdown
        isOpen={(isOpen ?? false) && !disabled}
        uuid={uuid}
        options={options}
        width={width}
        currentName={currentName}
        handleChange={handleChange}
        hasSearchInput={hasSearchInput}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $disabled?: boolean; $width: string }>`
  @media (max-width: 990px) {
    width: 100%;
  }

  width: ${({ $width }) => $width};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const StyledSelect = styled.div<{
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  width: 100%;
  height: 55px;
  padding: 0 17px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  outline: none;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    height: 48px;
    padding: 0 14px;
    gap: 8px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    height: 44px;
    padding: 0 12px;
    gap: 6px;
    border-radius: 4px;
  }

  ${({ $disabled, $hasError }) =>
    `
      border-color: ${$disabled ? "#cccccc" : $hasError ? COLOR_RED : "#cccccc"
    };
      background-color: ${$disabled ? "#f2f2f2" : $hasError ? COLOR_LIGHT_RED : "#ffffff"
    };
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
    $hasError ? COLOR_RED : "#0A2463"};
  }
`;

const StyledPlaceholder = styled.div<{
  $isPlaceholder: boolean;
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? "#999999" : "#000000")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 80%;

  @media (max-width: 990px) {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    max-width: 75%;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    max-width: 70%;
  }
`;

const rotate = ($isOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  transform: ${({ $isOpen }) =>
    $isOpen === null
      ? "rotate(0deg)"
      : $isOpen
        ? "rotate(180deg)"
        : "rotate(0deg)"};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${COLOR_RED};

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const StyledRequiredAsterisk = styled.span`
  color: ${COLOR_RED};
`;
